import React, { useEffect, useMemo, useState } from "react";
import "./ProductCard.css";
import { IconButton, Snackbar, Alert } from "@mui/material";
import { Favorite, FavoriteBorder, ModeComment } from "@mui/icons-material";
import { teal } from "@mui/material/colors";
import { Product } from "../../../types/productTypes";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { AddProductToWishList } from "../../../State/customer/wishlistSlice";

const ProductCard = ({ item }: { item: Product }) => {
    const [currentImage, setCurrentImage] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { wishList } = useAppSelector((state) => state.wishlist);
    const isWishlisted = wishList?.products?.some((p: Product) => p.id === item.id) ?? false;

    // Safely parse and normalize image sources
    const images: string[] = useMemo(() => {
        const raw = item?.images ?? (item as any)?.productImages ?? [];

        if (Array.isArray(raw)) return raw;

        if (typeof raw === "string") {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [raw];
            } catch {
                return [raw]; // Fallback if string is a single direct URL string
            }
        }

        return [];
    }, [item]);

    const truncatedTitle = item.title && item.title.length > 20 
        ? `${item.title.substring(0, 18)}...` 
        : item.title;

    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        if (isHovered && images.length > 1) {
            intervalId = setInterval(() => {
                setCurrentImage((prevImage) => (prevImage + 1) % images.length);
            }, 1000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [isHovered, images.length]);

    useEffect(() => {
        if (!isHovered) setCurrentImage(0);
    }, [isHovered]);

    const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") return;
        setSnackbarOpen(false);
    };

    const handleWishlist = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (item.id) {
            dispatch(AddProductToWishList({ productId: item.id }))
                .unwrap()
                .then(() => {
                    setSnackbarMessage(
                        isWishlisted ? "Removed from wishlist" : "Added to wishlist"
                    );
                    setSnackbarOpen(true);
                })
                .catch((error) => {
                    console.error("Wishlist error:", error);
                });
        }
    };

    return (
        <>
            <div 
                onClick={() => navigate(`/product-details/${item.category?.categoryId}/${encodeURIComponent(item.title)}/${item.id}`)} 
                className="group w-full relative cursor-pointer border border-gray-100 rounded-lg p-2 sm:p-3 bg-white hover:shadow-md transition-shadow duration-300 flex flex-col justify-between gap-2 h-full"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="relative overflow-hidden aspect-square w-full bg-gray-50 flex items-center justify-center rounded-md border border-gray-100">
                    {images.length === 0 ? (
                        <div className="text-gray-300 text-xs">No Image</div>
                    ) : (
                        images.map((imgSrc, index) => (
                            <img 
                                key={index}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-in-out" 
                                src={imgSrc} 
                                alt={item.title} 
                                style={{ 
                                    transform: `translateX(${(index - currentImage) * 100}%)`,
                                    visibility: Math.abs(index - currentImage) > 1 ? "hidden" : "visible"
                                }}
                            />
                        ))
                    )}

                    {isHovered && images.length > 1 && (
                        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1 z-10">
                            {images.map((_, idx) => (
                                <span 
                                    key={idx} 
                                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                                        idx === currentImage ? "bg-teal-500 w-3" : "bg-white/60"
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex flex-col justify-between flex-1 min-w-0 pt-0.5 gap-2">
                    <div className="space-y-0.5">
                        <p className="text-gray-800 font-medium text-xs sm:text-sm min-h-[1.0rem]">
                            {truncatedTitle}
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <div className="price flex flex-wrap items-center gap-1 text-[11px] sm:text-[14px]">
                            <span className="font-semibold text-gray-900">
                                Ksh {(item.sellingPrice ?? 0).toLocaleString()}
                            </span>
                            {item.mrpPrice > item.sellingPrice && (
                                <>
                                    <span className="line-through text-gray-400 scale-90 origin-left">
                                        Ksh {(item.mrpPrice ?? 0).toLocaleString()}
                                    </span>
                                    <span className="text-emerald-600 font-bold text-[9px] sm:text-[14px]">
                                        {item.discountPercent}% OFF
                                    </span>
                                </>
                            )}
                        </div>

                        <div className="flex gap-1.5 items-center">
                            <IconButton 
                                onClick={handleWishlist}
                                size="small"
                                sx={{ 
                                    bgcolor: isWishlisted ? "red.50" : "teal.50",
                                    color: isWishlisted ? "error.main" : teal[500],
                                    p: 0.5,
                                    "&:hover": { bgcolor: isWishlisted ? "red.100" : "teal.100" }
                                }}
                            >
                                {isWishlisted ? (
                                    <Favorite sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                ) : (
                                    <FavoriteBorder sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                )}
                            </IconButton>
                            <IconButton 
                                size="small"
                                sx={{ 
                                    bgcolor: "grey.100",
                                    color: "grey.600",
                                    p: 0.5,
                                    "&:hover": { bgcolor: "grey.200" }
                                }}
                            >
                                <ModeComment sx={{ fontSize: { xs: 16, sm: 18 } }} />
                            </IconButton>
                        </div>
                    </div>
                </div>
            </div>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ProductCard;